import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { PopularRoute } from '@/types/segments';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

interface RouteSuggestion extends PopularRoute {
  ai_score?: number;
  ai_description?: string;
  highlights?: string[];
  estimated_time_min?: number;
}

export const useRouteSuggestions = (userId: string | null) => {
  const [suggestions, setSuggestions] = useState<RouteSuggestion[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchSuggestions = async (
    lat: number,
    lng: number,
    desiredDistance?: number,
    difficulty?: string,
    surfacePreference?: string
  ) => {
    if (!userId) {
      toast.error('Você precisa estar autenticado');
      return;
    }

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/suggest-routes`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            lat,
            lng,
            desiredDistance,
            difficulty,
            surfacePreference,
          }),
        }
      );

      if (!response.ok) {
        const status = response.status;
        logger.error('Error fetching route suggestions', new Error(`HTTP ${status}`), { 
          userId, 
          lat, 
          lng, 
          status,
          statusText: response.statusText 
        });
        
        if (status === 401 || status === 403) {
          toast.error('Erro de autenticação. Faça login novamente.');
        } else if (status === 404) {
          toast.info('Ainda não há rotas cadastradas na sua região');
        } else {
          toast.error('Erro ao conectar com o servidor');
        }
        
        setSuggestions([]);
        setLoading(false);
        return;
      }

      const { routes } = await response.json();
      setSuggestions(routes || []);
      
      if (routes && routes.length > 0) {
        toast.success(`${routes.length} rotas sugeridas encontradas!`);
      } else {
        toast.info('Nenhuma rota encontrada para os filtros selecionados');
      }
    } catch (error) {
      logger.error('Error fetching route suggestions', error as Error, { userId, lat, lng });
      
      if (!navigator.onLine) {
        toast.error('Sem conexão com a internet');
      } else {
        toast.error('Ainda não há rotas disponíveis na sua região');
      }
      
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const saveRoute = async (routeId: string) => {
    if (!userId) return;

    try {
      const { error } = await (supabase as any)
        .from('saved_routes')
        .insert({
          user_id: userId,
          route_id: routeId,
        });

      if (error) throw error;
      toast.success('Rota salva com sucesso!');
    } catch (error: any) {
      if (error.code === '23505') {
        toast.info('Esta rota já está salva');
      } else {
        logger.error('Error saving route', error as Error, { userId, routeId });
        toast.error('Erro ao salvar rota');
      }
    }
  };

  const unsaveRoute = async (routeId: string) => {
    if (!userId) return;

    try {
      const { error } = await (supabase as any)
        .from('saved_routes')
        .delete()
        .eq('user_id', userId)
        .eq('route_id', routeId);

      if (error) throw error;
      toast.success('Rota removida dos salvos');
    } catch (error) {
      logger.error('Error unsaving route', error as Error, { userId, routeId });
      toast.error('Erro ao remover rota');
    }
  };

  return {
    suggestions,
    loading,
    fetchSuggestions,
    saveRoute,
    unsaveRoute,
  };
};
